"use client"

import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type SetStateAction } from "react"

import { usePathname, useSearchParams } from "next/navigation"

import {
  notifyBrowseReadiness,
  registerBrowseReadiness,
  browseEntryId,
  readBrowseValue,
  registerBrowseCapture,
  subscribeBrowseHistory,
  subscribeBrowseSnapshots,
  writeBrowseValue,
} from "@/platform/app-mode/browse-history"

export const useBrowseEntry = (renderedQuery?: string) => {
  const entryId = useSyncExternalStore(subscribeBrowseHistory, browseEntryId, () => "server")
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const query = renderedQuery ?? searchParams?.toString()
  const committed = useRef(entryId)
  if (typeof window !== "undefined" && (!pathname || (pathname === window.location.pathname && query === new URLSearchParams(window.location.search).toString())))
    committed.current = entryId
  return committed.current
}

export const useBrowseReadiness = (ready: boolean, entryId: string): void => {
  const latest = useRef(ready)
  latest.current = ready
  useLayoutEffect(() => registerBrowseReadiness(() => latest.current, entryId), [entryId])
  useLayoutEffect(notifyBrowseReadiness, [ready, entryId])
}

export const useBrowseValue = (key: string, initial: number, renderedQuery?: string) => {
  const entryId = useBrowseEntry(renderedQuery)
  const [state, setState] = useState(() => ({ entryId, key, value: readBrowseValue(key, initial, entryId) }))
  const value = state.entryId === entryId && state.key === key ? state.value : readBrowseValue(key, initial, entryId)
  if (state.entryId !== entryId || state.key !== key) setState({ entryId, key, value })
  const latest = useRef(value)
  latest.current = value
  useEffect(() => registerBrowseCapture(() => writeBrowseValue(key, latest.current, entryId), entryId), [entryId, key])
  useEffect(() => subscribeBrowseSnapshots(() => setState({ entryId, key, value: readBrowseValue(key, initial, entryId) })), [entryId, key, initial])
  const setValue = useCallback(
    (next: SetStateAction<number>) =>
      setState((current) => ({
        entryId,
        key,
        value: typeof next === "function" ? next(current.entryId === entryId && current.key === key ? current.value : readBrowseValue(key, initial, entryId)) : next,
      })),
    [entryId, key, initial]
  )
  return [value, setValue, entryId] as const
}
